import React, { useEffect, useMemo, useState } from 'react';
import {
  useFlowObserverPanelStore,
  readOutputDataFromLastExecution,
  readLastKnownData,
  readNodeConfig,
  fetchPinnedExecutionSnapshot,
} from '@flow-studio/flow-observer-core';
import type { ExecutionLogEntry } from '@flow-studio/flow-observer-core';
import { DataItemsView } from '../common/DataItemsView.js';
import { WebJsonField } from '../shared/WebJsonField.js';
import { FullscreenPanel } from '../common/FullscreenPanel.js';
import { ExecutionLogsTabContent } from './ExecutionLogsTabContent.js';
import './NodeDetailPanel.css';

type DetailTab = 'output' | 'outputItems' | 'pinned' | 'lastKnown' | 'config' | 'all' | 'logs';

const TABS: { key: DetailTab; label: string }[] = [
  { key: 'output', label: 'Output' },
  { key: 'outputItems', label: 'Output Items' },
  { key: 'pinned', label: 'Pinned Data' },
  { key: 'lastKnown', label: 'Last Known Data' },
  { key: 'config', label: 'Config' },
  { key: 'all', label: 'All' },
  { key: 'logs', label: 'Node Execution Logs' },
];

export interface NodeDetailPanelProps {
  nodeKey: string;
}

/** outputData/inputData is always `{ items: [{json: ...}] }`; some entries (e.g. errors) may not have the wrapper. */
function extractItems(data: unknown): Array<{ json?: unknown } | unknown> {
  if (data && typeof data === 'object' && Array.isArray((data as any).items)) {
    return (data as any).items;
  }
  return data === undefined || data === null ? [] : [data];
}

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="fop-empty">
    <span>{message}</span>
  </div>
);

export const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({ nodeKey }) => {
  const [activeTab, setActiveTab] = useState<DetailTab>('output');
  // undefined = not yet fetched, null = fetched but unavailable — distinguishes "haven't tried" from "tried, nothing there".
  const [pinnedData, setPinnedData] = useState<{ input: unknown; output: unknown } | null | undefined>(undefined);
  const [pinnedLoading, setPinnedLoading] = useState(false);

  const executionLogBuffer = useFlowObserverPanelStore(
    (s) => (s.tabRuntimeStates['execution-logs']?.dataBuffer ?? []) as ExecutionLogEntry[]
  );

  useEffect(() => {
    setPinnedData(undefined);
  }, [nodeKey]);

  const output = useMemo(() => readOutputDataFromLastExecution(executionLogBuffer, nodeKey), [executionLogBuffer, nodeKey]);
  const lastKnown = useMemo(() => readLastKnownData(executionLogBuffer, nodeKey), [executionLogBuffer, nodeKey]);
  const config = useMemo(() => readNodeConfig(executionLogBuffer, nodeKey), [executionLogBuffer, nodeKey]);

  const handleTabClick = (tab: DetailTab) => {
    setActiveTab(tab);
    // On-demand fetch, per the prompt — not preloaded on every node click, only when the tab is actually opened.
    if (tab === 'pinned' && pinnedData === undefined && !pinnedLoading) {
      setPinnedLoading(true);
      fetchPinnedExecutionSnapshot(nodeKey)
        .then(setPinnedData)
        .finally(() => setPinnedLoading(false));
    }
  };

  const renderOutput = () =>
    output ? <WebJsonField value={output.outputData} /> : <EmptyState message={`No completed execution found for ${nodeKey}`} />;

  const renderOutputItems = () =>
    output ? (
      <DataItemsView items={extractItems(output.outputData)} />
    ) : (
      <EmptyState message={`No completed execution found for ${nodeKey}`} />
    );

  const renderPinned = () => {
    if (pinnedLoading) return <EmptyState message="Loading pinned data…" />;
    if (!pinnedData) return <EmptyState message="No pinned data available for this node" />;
    return (
      <div className="fop-node-detail__side-by-side">
        <div>
          <div className="fop-node-detail__pane-title">Input</div>
          <DataItemsView items={extractItems(pinnedData.input)} />
        </div>
        <div>
          <div className="fop-node-detail__pane-title">Output</div>
          <DataItemsView items={extractItems(pinnedData.output)} />
        </div>
      </div>
    );
  };

  const renderLastKnown = () => (
    <div className="fop-node-detail__side-by-side">
      <div>
        <div className="fop-node-detail__pane-title">Initiated (Input)</div>
        {lastKnown.initiated ? (
          <DataItemsView items={extractItems((lastKnown.initiated.rawDetail as any)?.inputData)} />
        ) : (
          <EmptyState message="No Initiated record found" />
        )}
      </div>
      <div>
        <div className="fop-node-detail__pane-title">Completed (Output)</div>
        {lastKnown.completed ? (
          <DataItemsView items={extractItems((lastKnown.completed.rawDetail as any)?.outputData)} />
        ) : (
          <EmptyState message="No Completed record found" />
        )}
      </div>
    </div>
  );

  const renderConfig = () =>
    config ? <WebJsonField value={config} /> : <EmptyState message={`No resolved configuration found for ${nodeKey}`} />;

  const renderAll = () => (
    <div className="fop-node-detail__all">
      <div>
        <div className="fop-node-detail__pane-title">Input</div>
        {lastKnown.initiated ? (
          <DataItemsView items={extractItems((lastKnown.initiated.rawDetail as any)?.inputData)} />
        ) : (
          <EmptyState message="No input available" />
        )}
      </div>
      <div>
        <div className="fop-node-detail__pane-title">Config</div>
        {config ? <WebJsonField value={config} /> : <EmptyState message="No config available" />}
      </div>
      <div>
        <div className="fop-node-detail__pane-title">Output</div>
        {output ? <WebJsonField value={output.outputData} /> : <EmptyState message="No output available" />}
      </div>
    </div>
  );

  const renderLogs = () => (
    <ExecutionLogsTabContent
      tabId="execution-logs"
      loadState="loaded"
      onRetry={() => {}}
      nodeKeyFilter={nodeKey}
      storageKeyPrefix={`node-logs-${nodeKey}`}
    />
  );

  const activeLabel = TABS.find((t) => t.key === activeTab)?.label;

  return (
    <div className="fop-node-detail">
      <div className="fop-node-detail__tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`fop-node-detail__tab ${activeTab === tab.key ? 'fop-node-detail__tab--active' : ''}`}
            onClick={() => handleTabClick(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="fop-node-detail__content">
        <FullscreenPanel title={activeLabel}>
          {activeTab === 'output' && renderOutput()}
          {activeTab === 'outputItems' && renderOutputItems()}
          {activeTab === 'pinned' && renderPinned()}
          {activeTab === 'lastKnown' && renderLastKnown()}
          {activeTab === 'config' && renderConfig()}
          {activeTab === 'all' && renderAll()}
          {activeTab === 'logs' && renderLogs()}
        </FullscreenPanel>
      </div>
    </div>
  );
};
